import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    Alert, Box, Paper, Skeleton, Snackbar, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import ActionButton from '../../lib/ActionButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import { sortDatasets } from '../../lib/dataTransforms';
import { useAdminVisibility } from '../../lib/useAdminVisibility';
import { useCreateDatasetMutation } from '../../state/apiSlice';
import { setSelectedID } from '../../state/datasetSlice';

import AdminDatasetRow from '../admin/AdminDatasetRow';
import CreateDatasetDialog from '../admin/CreateDatasetDialog';
import MqttConnectGuide from '../admin/MqttConnectGuide';

const TABLE_COL_COUNT = 7; // chevron, logo, name, org, role, access, mqtt

// crypto.randomUUID is available in all evergreen browsers; fall back to a
// time-based pseudo-UUID for older WebViews. We don't need cryptographic
// strength for an idempotency key — uniqueness is enough.
const newIdempotencyKey = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `t${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Skeleton row that matches the real row's layout, used while datasets load.
 * Avoids the layout shift the previous CircularProgress caused.
 */
function SkeletonRow() {
    return (
        <TableRow>
            <TableCell padding="checkbox" align="center">
                <Skeleton variant="circular" width={28} height={28} />
            </TableCell>
            <TableCell padding="checkbox" align="center">
                <Skeleton variant="rectangular" width={64} height={20} />
            </TableCell>
            <TableCell><Skeleton variant="text" width={140} /></TableCell>
            <TableCell><Skeleton variant="text" width={100} /></TableCell>
            <TableCell><Skeleton variant="rounded" width={70} height={20} /></TableCell>
            <TableCell><Skeleton variant="rounded" width={80} height={20} /></TableCell>
            <TableCell><Skeleton variant="rounded" width={120} height={20} /></TableCell>
        </TableRow>
    );
}

/**
 * Centred empty-state shown when the user has the create-dataset capability
 * but doesn't yet administer any datasets. Single primary CTA, far better
 * conversion than the previous "use the button up there →" copy. (DES23)
 */
function FirstDatasetCallToAction({ onCreate }) {
    return (
        <Paper variant="outlined" sx={{ textAlign: 'center', py: 6, px: 2, mt: 2 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h5" gutterBottom>Create your first dataset</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Datasets you create or are admin on will appear here.
            </Typography>
            <ActionButton
                variant="contained"
                size="large"
                startIcon={<AddCircleIcon />}
                onClick={onCreate}
            >
                Create dataset
            </ActionButton>
        </Paper>
    );
}

FirstDatasetCallToAction.propTypes = {
    onCreate: require('prop-types').func.isRequired
};

/**
 * Admin / My MQTT tab — the page admins land on to manage their datasets,
 * and that read-only viewers land on to inspect MQTT status of any dataset
 * they can read. Same component, two modes:
 *
 *   mode === 'admin' — full UI: Create button, action buttons on each row
 *                      (Set up / Edit / Delete MQTT, Open in Overview),
 *                      empty-state CTA when the user can create but
 *                      doesn't admin anything yet.
 *   mode === 'user'  — read-only: no Create button, no per-row actions
 *                      besides Open in Overview, neutral empty-state copy.
 *                      Same MqttConnectGuide accordion at the top, same
 *                      MqttInfo display, same refresh control.
 *
 * Both modes share the dataset table layout. The difference is which
 * datasets feed into it and which buttons are exposed.
 */
export default function Admin() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        visible, mode, canCreateDataset, adminDatasets, accessibleDatasets,
        isLoading
    } = useAdminVisibility();

    const isAdminMode = mode === 'admin';
    const isUserMode = mode === 'user';

    const [createOpen, setCreateOpen] = useState(false);
    // Re-generate the idempotency key whenever the user opens the dialog
    // afresh. Reusing it across opens would mean a successful 1st create
    // followed by clicking "Create dataset" again replays the cached
    // response — the user wouldn't be able to create a real second
    // dataset until the cache TTL expired (10 minutes server-side).
    const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
    useEffect(() => {
        if (createOpen) setIdempotencyKey(newIdempotencyKey());
    }, [createOpen]);
    // Snackbar lives at the page level so all admin actions can fire it.
    // (DES27 — replaces silent dialog-closes everywhere in the admin flow.)
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
    const [createDataset] = useCreateDatasetMutation();

    // Different source list per mode. Admins see datasets they
    // administer; users see every accessible dataset (own role !=
    // 'none' OR open). Sorting is identical.
    const sourceDatasets = isAdminMode ? adminDatasets : accessibleDatasets;
    const sorted = sortDatasets(sourceDatasets, 'asc', 'DatasetName');

    const handleCreateSubmit = async (body) => {
        await createDataset({ body, idempotencyKey }).unwrap();
    };

    const fireSnack = (message, severity = 'success') => {
        setSnack({ open: true, message, severity });
    };

    // The tab should never be visible at all if !visible, but render a soft empty
    // state defensively in case it's reached (e.g., bookmarked URL).
    if (!isLoading && !visible) {
        return (
            <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                You don&apos;t have admin access to any dataset, and your account
                doesn&apos;t have the create-dataset capability.
            </Typography>
        );
    }

    // Empty-state CTA only fires in admin mode (the "create your first
    // dataset" copy + button). User mode shows neutral copy in the
    // table body when there are no rows — see the TableBody empty case
    // below.
    const showFirstDatasetCta = !isLoading && isAdminMode && canCreateDataset && sorted.length === 0;

    // Headline copy switches by mode so the page never feels mis-titled
    // for non-admins ("Manage…" implies actions they don't have).
    const pageTitle = isAdminMode ? 'Admin' : 'My MQTT';
    const pageSubtitle = isAdminMode
        ? 'Manage datasets you own or administer. Expand a row to see MQTT status and admin actions.'
        : 'View live MQTT status for the datasets you have access to. Expand a row to see broker details.';

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
                justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminPanelSettingsIcon fontSize="large" />
                    <Typography variant="h4" gutterBottom={false}>
                        {pageTitle}
                    </Typography>
                </Box>
                {/*
                  Create dataset is admin-only. Even if a stray
                  canCreateDataset flag came through in user mode (the
                  hook already gates this, but defence in depth), the
                  isAdminMode check here is the one place to look.
                */}
                {isAdminMode && canCreateDataset && (
                    <ActionButton
                        variant="contained"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Create dataset
                    </ActionButton>
                )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pageSubtitle}
            </Typography>

            {/* "How to connect to MQTT" — shown to both modes. The
                connection mechanics are identical for admins and users:
                generate a JWT on My JWTs, present it as the MQTT
                password (or Bearer header). Default-folded so the table
                stays the primary content. */}
            <MqttConnectGuide />

            {showFirstDatasetCta ? (
                <FirstDatasetCallToAction onCreate={() => setCreateOpen(true)} />
            ) : (
                <TableContainer component={Paper}>
                    <Table aria-label="admin-datasets-table">
                        <TableHead>
                            <TableRow>
                                <TableCell width="1%" />
                                <TableCell width="5%" />
                                <TableCell>Dataset</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Your role</TableCell>
                                <TableCell>Access</TableCell>
                                <TableCell>MQTT</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <>
                                    <SkeletonRow />
                                    <SkeletonRow />
                                    <SkeletonRow />
                                </>
                            )}
                            {!isLoading && sorted.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={TABLE_COL_COUNT} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            {isUserMode
                                                ? 'You don\u2019t have any MQTT-enabled datasets. Ask the dataset owner to set up MQTT, or visit the Catalog to discover open datasets.'
                                                : 'You don\u2019t administer any datasets.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading && sorted.map(ds => (
                                <AdminDatasetRow
                                    key={ds.DatasetID}
                                    ds={ds}
                                    mode={mode}
                                    onEditMetadata={(d) => {
                                        dispatch(setSelectedID(d.DatasetID));
                                        // TabSelect's URL → Redux mirror
                                        // (F3/F4) means navigate alone is
                                        // enough — no manual dispatch(setTab)
                                        // needed.
                                        navigate('/tabselect/Inspector');
                                    }}
                                    onMqttSuccess={(verb, d) => {
                                        fireSnack(`MQTT ${verb} for ${d.DatasetName}`, 'success');
                                    }}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* CreateDatasetDialog only mounts in admin mode — its
                trigger (the Create button) doesn't exist in user mode,
                so the dialog can never open there. Keeping it
                conditionally mounted matches the pattern used in
                AdminDatasetRow for the per-row dialogs. */}
            {isAdminMode && (
                <CreateDatasetDialog
                    open={createOpen}
                    onSubmit={handleCreateSubmit}
                    onSuccess={() => fireSnack('Dataset created', 'success')}
                    onClose={() => setCreateOpen(false)}
                />
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={4500}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack(s => ({ ...s, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
